import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useTaskManager() {
  const [tasks, setTasks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [tasksRes, templatesRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/templates`),
      ]);
      setTasks(tasksRes.data.tasks || tasksRes.data);
      setTemplates(templatesRes.data.templates || templatesRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Task CRUD
  const createTask = async (name, category) => {
    try {
      const res = await axios.post(`${API}/tasks`, { name, category });
      setTasks((prev) => [...prev, res.data]);
      toast.success("Task created");
    } catch {
      toast.error("Failed to create task");
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      const res = await axios.patch(`${API}/tasks/${taskId}`, updates);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? res.data : t)));
    } catch {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Task deleted");
    } catch {
      toast.error("Failed to delete task");
    }
  };

  const toggleTask = async (taskId) => {
    try {
      const res = await axios.patch(`${API}/tasks/${taskId}/toggle`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? res.data : t)));
    } catch {
      toast.error("Failed to toggle task");
    }
  };

  const updateQuadrant = async (taskId, quadrant) => {
    try {
      const res = await axios.patch(`${API}/tasks/${taskId}/quadrant`, { quadrant });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? res.data : t)));
    } catch {
      toast.error("Failed to move task");
    }
  };

  const toggleUrgent = async (taskId) => {
    try {
      const res = await axios.patch(`${API}/tasks/${taskId}/urgent`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? res.data : t)));
      setEditingTask((prev) => prev && prev.id === taskId ? res.data : prev);
    } catch {
      toast.error("Failed to toggle urgent flag");
    }
  };

  // Subtask operations
  const addSubtask = async (taskId, name) => {
    try {
      const res = await axios.post(`${API}/tasks/${taskId}/subtasks`, { name });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? res.data : t)));
      setEditingTask((prev) => prev && prev.id === taskId ? res.data : prev);
    } catch {
      toast.error("Failed to add sub-task");
    }
  };

  const toggleSubtask = async (taskId, subtaskId) => {
    try {
      const res = await axios.patch(`${API}/tasks/${taskId}/subtasks/${subtaskId}/toggle`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? res.data : t)));
      setEditingTask((prev) => prev && prev.id === taskId ? res.data : prev);
    } catch {
      toast.error("Failed to toggle sub-task");
    }
  };

  const deleteSubtask = async (taskId, subtaskId) => {
    try {
      const res = await axios.delete(`${API}/tasks/${taskId}/subtasks/${subtaskId}`);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? res.data : t)));
      setEditingTask((prev) => prev && prev.id === taskId ? res.data : prev);
    } catch {
      toast.error("Failed to delete sub-task");
    }
  };

  // Template operations
  const createTemplate = async (name, category, notes) => {
    try {
      const res = await axios.post(`${API}/templates`, { name, category, notes });
      setTemplates((prev) => [...prev, res.data]);
      toast.success("Template saved");
    } catch {
      toast.error("Failed to create template");
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      setTemplates((prev) => prev.filter((t) => t.id !== templateId));
      toast.success("Template deleted");
    } catch {
      toast.error("Failed to delete template");
    }
  };

  const instantiateTemplate = async (templateId) => {
    try {
      const res = await axios.post(`${API}/templates/${templateId}/instantiate`);
      setTasks((prev) => [...prev, res.data]);
      toast.success("Task added from template");
    } catch {
      toast.error("Failed to add from template");
    }
  };

  const saveAsTemplate = async (taskId) => {
    try {
      const res = await axios.post(`${API}/tasks/${taskId}/to-template`);
      setTemplates((prev) => [...prev, res.data]);
      toast.success("Saved as template");
    } catch {
      toast.error("Failed to save as template");
    }
  };

  // Board operations
  const clearCompleted = async () => {
    try {
      const res = await axios.post(`${API}/board/clear-completed`);
      setTasks((prev) => prev.filter((t) => !t.completed));
      toast.success(`Cleared ${res.data.cleared} completed tasks`);
    } catch {
      toast.error("Failed to clear completed");
    }
  };

  const resetBoard = async (label) => {
    try {
      const res = await axios.post(`${API}/board/reset`, { label });
      if (res.data.archive_id) {
        setTasks([]);
        toast.success(`Archived ${res.data.archived_tasks} tasks. Fresh start!`);
      } else {
        toast("No tasks to archive");
      }
    } catch {
      toast.error("Failed to reset board");
    }
  };

  return {
    tasks, templates, loading, editingTask, setEditingTask,
    fetchData, createTask, updateTask, deleteTask, toggleTask,
    updateQuadrant, toggleUrgent,
    addSubtask, toggleSubtask, deleteSubtask,
    createTemplate, deleteTemplate, instantiateTemplate, saveAsTemplate,
    clearCompleted, resetBoard,
  };
}
